import { getLocalTimeZone, parseDate, today } from "@internationalized/date";
import { Calendar as CalendarIcon } from "@untitledui/icons";
import type { ReactNode } from "react";
import {
  Button as AriaButton,
  DatePicker as AriaDatePicker,
  Dialog as AriaDialog,
  Group as AriaGroup,
  Popover as AriaPopover,
  type DateValue,
} from "react-aria-components";
import { Button } from "@/components/base/buttons/button";
import { HintText } from "@/components/base/input/hint-text";
import { Label } from "@/components/base/input/label";
import { cx } from "@/utils/cx";
import { Calendar } from "./calendar";

const highlightedDates = [today(getLocalTimeZone())];

const pad = (n: number) => String(n).padStart(2, "0");

interface DatePickerInputProps {
  value?: string | null;
  onChange?: (value: string | null) => void;
  label?: string;
  isRequired?: boolean;
  hint?: ReactNode;
  isInvalid?: boolean;
  onApply?: () => void;
  onCancel?: () => void;
  minValue?: DateValue;
}

export const DatePickerInput = ({
  value,
  onChange,
  label,
  isRequired,
  hint,
  isInvalid,
  onApply,
  onCancel,
  minValue,
}: DatePickerInputProps) => {
  const dateValue = value ? parseDate(value) : null;
  const formattedDate = dateValue ? `${pad(dateValue.day)}/${pad(dateValue.month)}/${dateValue.year}` : null;

  return (
    <div className="flex flex-col gap-1.5">
      {label && <Label isRequired={isRequired}>{label}</Label>}

      <AriaDatePicker
        shouldCloseOnSelect={false}
        value={dateValue}
        onChange={(newValue) => onChange?.(newValue ? newValue.toString() : null)}
      >
        <AriaGroup
          isInvalid={isInvalid}
          className={({ isFocusWithin, isInvalid }) =>
            cx(
              "flex w-full flex-row items-center rounded-lg bg-primary px-3.5 py-2.5 shadow-none ring-1 ring-primary ring-inset transition-shadow duration-100 ease-linear",
              isFocusWithin && "ring-2 ring-brand",
              isInvalid && "ring-error_subtle",
              isInvalid && isFocusWithin && "ring-2 ring-error",
            )
          }
        >
          <AriaButton className="flex flex-1 items-center justify-between outline-hidden">
            <span className={cx("text-md", formattedDate ? "text-primary" : "text-placeholder")}>
              {formattedDate ?? "dd/mm/yyyy"}
            </span>
            <CalendarIcon className="size-5 text-fg-quaternary" />
          </AriaButton>
        </AriaGroup>
        <AriaPopover
          offset={8}
          placement="bottom right"
          className={({ isEntering, isExiting }) =>
            cx(
              "z-[130] origin-(--trigger-anchor-point) will-change-transform",
              isEntering &&
                "duration-150 ease-out animate-in fade-in placement-right:slide-in-from-left-0.5 placement-top:slide-in-from-bottom-0.5 placement-bottom:slide-in-from-top-0.5",
              isExiting &&
                "duration-100 ease-in animate-out fade-out placement-right:slide-out-to-left-0.5 placement-top:slide-out-to-bottom-0.5 placement-bottom:slide-out-to-top-0.5",
            )
          }
        >
          <AriaDialog className="rounded-2xl bg-primary shadow-none ring ring-secondary_alt">
            {({ close }) => (
              <>
                <div className="flex px-6 py-5">
                  <Calendar highlightedDates={highlightedDates} minValue={minValue} />
                </div>
                <div className="grid grid-cols-2 gap-3 border-t border-secondary p-4">
                  <Button
                    size="md"
                    color="secondary"
                    onClick={() => {
                      onCancel?.();
                      close();
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="md"
                    color="primary"
                    onClick={() => {
                      onApply?.();
                      close();
                    }}
                  >
                    Apply
                  </Button>
                </div>
              </>
            )}
          </AriaDialog>
        </AriaPopover>
      </AriaDatePicker>

      {hint && <HintText isInvalid={isInvalid}>{hint}</HintText>}
    </div>
  );
};
